// A form with unsaved changes warns before the screen that holds it goes away.
//
// Three things take a half-edited form off the screen, and they go through
// three different mechanisms, so one hook covers all three:
//
// - a client-side navigation — the sidebar, a breadcrumb, Cancel's
//   `history.goBack()`, the browser's Back — goes through the router's history,
//   which is blocked while anything registered here is dirty;
// - a full navigation — a plain `href`, `window.location = …`, closing the tab,
//   a reload — fires `beforeunload`, which is answered the same way;
// - a tab switch in `ContentTabs`, which is not a navigation at all: it is a
//   `setState` that unmounts the body (`key={selected}`), and the form's state
//   goes with it. The tab strip asks here before it switches.
//
// The third is why the registry is SCOPED and not one flat set. The page editor
// and the site screen put their tabs INSIDE one Mutation — one form, several
// tabs over its state — and switching those tabs loses nothing, so it must not
// prompt. A tab strip therefore only asks about the checkers registered under
// its own body; a checker registers in every scope from the nearest one up to
// the root, and the root is what the router and `beforeunload` consult.
//
// The dirty-check is a FUNCTION, read at the moment somebody tries to leave and
// never cached: `Section.Mutation` navigates from inside Apollo's `onCompleted`,
// synchronously, before React has rendered the saved state, and a boolean taken
// at render time would still say dirty. See `Mutation` for the flag it reads.
//
// For a router transition the check is handed the destination. Not every
// navigation leaves a form: the page editor addresses an element as a route
// UNDER its own — `/edit/element/:index` — and the form stays mounted across
// that, so a checker that knows its own screen can answer "still here" for a
// destination inside it. `beforeunload` and a tab switch hand nothing, because
// both always take the form away.
//
// The prompt is the browser's own `confirm`. `beforeunload` cannot show
// anything else, and the router's block goes through the same `window.confirm`
// by default, so the two departures read the same. Nothing here renders.
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

export const MESSAGE =
  "You have unsaved changes. Leave this page and lose them?";

const createScope = (parent) => ({ parent, checkers: new Set() });

// The root: what the router's block and `beforeunload` consult.
export const ROOT = createScope(null);

const ScopeContext = createContext(ROOT);

/** The scope the calling component registers into. */
export const useScope = () => useContext(ScopeContext);

/**
 * A scope of this component's own, a child of the one it is rendered in.
 * Handed to `ScopeProvider` around whatever this component may unmount on its
 * own, and to `hasUnsavedChanges` / `confirmDiscard` before doing so.
 */
export const useChildScope = () => {
  const parent = useContext(ScopeContext);
  const [scope] = useState(() => createScope(parent));
  return scope;
};

export const ScopeProvider = ({ scope, children }) => (
  <ScopeContext.Provider value={scope}>{children}</ScopeContext.Provider>
);

/**
 * Whether anything registered under `scope` (the root by default) is dirty.
 * `location` is the router destination when there is one; see above.
 */
export const hasUnsavedChanges = (scope = ROOT, location = undefined) => {
  for (const isDirty of scope.checkers) {
    if (isDirty(location)) return true;
  }
  return false;
};

/**
 * Whether `pathname` is `prefix` itself or a path beneath it — the test a form
 * uses to say a destination is still its own screen.
 */
export const isWithin = (pathname, prefix) =>
  Boolean(prefix) && (pathname === prefix || pathname.startsWith(prefix + "/"));

/**
 * True when it is fine to go ahead: nothing under `scope` is dirty, or the
 * person confirmed losing what is.
 */
export const confirmDiscard = (scope = ROOT) =>
  !hasUnsavedChanges(scope) || window.confirm(MESSAGE);

// One block on the history and one `beforeunload` listener for the whole app,
// held while at least one checker is registered: a history supports only one
// prompt at a time, and the check is over the root anyway.
let holders = 0;
let unblock = null;

const onBeforeUnload = (event) => {
  if (!hasUnsavedChanges()) return undefined;
  event.preventDefault();
  // What legacy browsers show; modern ones show their own text and only need
  // the event cancelled. Both keep the page.
  event.returnValue = MESSAGE;
  return MESSAGE;
};

const acquire = (history) => {
  if (holders++ > 0) return;
  // A string from the block callback is the prompt; anything else lets the
  // transition through. Read at transition time, never at render.
  unblock = history
    ? history.block((location) => (hasUnsavedChanges(ROOT, location) ? MESSAGE : undefined))
    : null;
  window.addEventListener("beforeunload", onBeforeUnload);
};

const release = () => {
  if (--holders > 0) return;
  if (unblock) unblock();
  unblock = null;
  window.removeEventListener("beforeunload", onBeforeUnload);
};

/**
 * Registers `isDirty` for as long as the calling component is mounted.
 *
 * `isDirty` is called when somebody tries to leave, so it may close over the
 * render's state freely — the latest one handed in is the one that runs. For a
 * router transition it receives the destination `location`; otherwise nothing.
 * Outside a Router (a unit test mounting a screen on its own) the history half
 * is skipped and only `beforeunload` and the scope are wired.
 */
export const useUnsavedChanges = (isDirty) => {
  const history = useHistory();
  const scope = useContext(ScopeContext);
  const latest = useRef(isDirty);
  latest.current = isDirty;

  useEffect(() => {
    const check = (location) => Boolean(latest.current(location));
    for (let current = scope; current; current = current.parent) {
      current.checkers.add(check);
    }
    acquire(history);
    return () => {
      for (let current = scope; current; current = current.parent) {
        current.checkers.delete(check);
      }
      release();
    };
  }, [history, scope]);
};
